import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Server, AlertCircle, MoreVertical, Code, FileText, RefreshCw, Trash2, Settings, Briefcase, Cloud } from 'lucide-react';
import { Skeleton } from '../components/ui/Skeleton';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../lib/api-client';
import { ContainerManagement } from '../components/ContainerManagement';
import { TenantHeading } from '../components/tenants/TenantLogo';
import { resolveTenantMeta, useTenantMetaMap } from '../hooks/useTenantMetaMap';
import { usePermission } from '../hooks/usePermission';
import { useActivityHeartbeat } from '../hooks/useActivityHeartbeat';
import { useCacheEvents } from '../hooks/useCacheEvents';
import { PageHeader } from '../components/ui/PageHeader';
import { DropdownMenu } from '../components/ui/DropdownMenu';
import { ToastContainer } from '../components/ui/Toast';
import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle } from '../components/ui/Dialog';
import { InlineConfirmation } from '../components/ui/InlineConfirmation';
import { Button } from '../components/ui/Button';
import { LastSynced } from '../components/ui/LastSynced';
import { getCloudProviderColor } from '../lib/colors';

type DevCloudProvider = 'hetzner' | 'oci' | 'aws';

interface DevInstance {
  id: string;
  name: string;
  status: string;
  instanceType: string;
  region: string;
  publicIp?: string;
  privateIp?: string;
  created: Date | string;
  provider?: DevCloudProvider | string;
  fqdn?: string;
}

interface ProviderGroup {
  provider: DevCloudProvider;
  instances: DevInstance[];
}

interface TenantGroup {
  tenant: string;
  tenantName: string;
  tenantColor?: string | null;
  providers: ProviderGroup[];
}


export function DevInstances() {
  const navigate = useNavigate();
  const { accessibleTenants, selectedTenants } = useAuth();
  const { isAllowed: canView } = usePermission('dev_instances', 'read');
  const tenantMetaMap = useTenantMetaMap(canView);
  const { isAllowed: canManageInstance } = usePermission('dev_instances', 'manage_instance');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tenantGroups, setTenantGroups] = useState<TenantGroup[]>([]);
  const [selectedServer, setSelectedServer] = useState<{ id: string; name: string } | null>(null);
  const [toasts, setToasts] = useState<Array<{ id: string; message: string; type?: 'success' | 'error' | 'warning' | 'info' }>>([]);
  const [rebuildTarget, setRebuildTarget] = useState<{ id: string; name: string; tenantCode: string } | null>(null);
  const [isRebuilding, setIsRebuilding] = useState(false);
  const [destroyTarget, setDestroyTarget] = useState<{ id: string; name: string } | null>(null);
  const [isDestroying, setIsDestroying] = useState(false);
  const [syncedAt, setSyncedAt] = useState<string | null>(null);

  const tenantsToQuery = selectedTenants.length > 0 ? selectedTenants : accessibleTenants;

  useActivityHeartbeat('cache:dev_instances', canView);

  const fetchDevInstances = useCallback(async () => {
    if (!canView) return;
    if (tenantsToQuery.length === 0) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);

    try {
      // Fetch all tenants to get their display names and colors
      const tenantsResponse = await apiClient.getTenants();
      const tenantsData = Array.isArray(tenantsResponse) ? tenantsResponse : tenantsResponse.tenants || [];
      const tenantMap: Map<string, { name: string; color?: string | null }> = new Map(
        tenantsData.map((t: any) => [t.code.toLowerCase(), { name: t.name, color: t.color }]),
      );

      const response = await apiClient.getDevInstances();
      const instances = response.instances || [];
      setSyncedAt(response.syncedAt ?? null);

      // Filter out PR instances (names starting with "pr" followed by a number)
      const devInstances = instances.filter((instance: DevInstance) => {
        const nameLower = instance.name.toLowerCase();
        // Exclude PR instances (e.g., "pr599.breedon")
        return !/^pr\d+\./.test(nameLower);
      });

      // Group instances by tenant (extracted from name after the dot), then by cloud provider
      const grouped = new Map<string, DevInstance[]>();

      devInstances.forEach((instance: DevInstance) => {
        // Extract tenant code from name (e.g., "dev.breedon" -> "breedon")
        const parts = instance.name.split('.');
        const tenantCode = parts.length > 1 ? parts[1].toLowerCase() : 'unknown';

        // Filter by selected tenants
        if (tenantsToQuery.length > 0 && !tenantsToQuery.some(t => t.toLowerCase() === tenantCode)) {
          return;
        }

        if (!grouped.has(tenantCode)) {
          grouped.set(tenantCode, []);
        }
        grouped.get(tenantCode)!.push(instance);
      });

      const providerOrder: DevCloudProvider[] = ['hetzner', 'oci', 'aws'];

      // Convert to array with tenant names/colors and provider sections
      const groups: TenantGroup[] = Array.from(grouped.entries()).map(([tenant, tenantInstances]) => {
        const tenantInfo = tenantMap.get(tenant);
        const byProvider = new Map<DevCloudProvider, DevInstance[]>();
        for (const instance of tenantInstances) {
          const raw = String(instance.provider || 'hetzner').toLowerCase();
          const provider: DevCloudProvider =
            raw === 'oci' || raw === 'oracle' ? 'oci' : raw === 'aws' ? 'aws' : 'hetzner';
          if (!byProvider.has(provider)) byProvider.set(provider, []);
          byProvider.get(provider)!.push(instance);
        }
        const providers: ProviderGroup[] = providerOrder
          .filter((p) => (byProvider.get(p) || []).length > 0)
          .map((provider) => ({ provider, instances: byProvider.get(provider)! }));
        return {
          tenant,
          tenantName: tenantInfo?.name || tenant,
          tenantColor: tenantInfo?.color ?? null,
          providers,
        };
      });

      // Sort by tenant name
      groups.sort((a, b) => a.tenantName.localeCompare(b.tenantName));

      setTenantGroups(groups);
    } catch (err: any) {
      console.error('Failed to fetch dev instances:', err);
      setError(err.message || 'Failed to load dev instances');
    } finally {
      setIsLoading(false);
    }
  }, [canView, tenantsToQuery.join(',')]);

  useEffect(() => {
    void fetchDevInstances();
  }, [fetchDevInstances]);

  // Refresh the list when the backend cache updates (SSE), so instances disappear/appear without a page reload.
  useCacheEvents({
    enabled: canView,
    cacheNames: ['dev_instances'],
    onEvent: () => {
      void fetchDevInstances();
    },
  });

  const getStatusBadge = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes('running') || statusLower === 'active') {
      return <Badge className="bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-800">Running</Badge>;
    } else if (statusLower.includes('stopped') || statusLower === 'inactive') {
      return <Badge className="bg-gray-50 text-gray-700 dark:bg-gray-800 dark:text-gray-300 border-gray-200 dark:border-gray-700">Stopped</Badge>;
    } else if (statusLower.includes('pending') || statusLower.includes('provisioning')) {
      return <Badge className="bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800">Pending</Badge>;
    } else {
      return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (date: Date | string) => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        {[1, 2].map((i) => (
          <div key={i} className="space-y-4">
            <Skeleton className="h-6 w-32" />
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map((j) => (
                <Card key={j}>
                  <CardHeader>
                    <Skeleton className="h-5 w-40 mb-2" />
                    <Skeleton className="h-4 w-24" />
                  </CardHeader>
                  <CardContent>
                    <Skeleton className="h-20 w-full" />
                  </CardContent>
                </Card>
              ))}
        </div>
        </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="py-8 text-center">
            <AlertCircle className="w-12 h-12 mx-auto text-red-600 dark:text-red-400 mb-4" />
            <p className="text-red-600 dark:text-red-400">Error: {error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalInstances = tenantGroups.reduce(
    (sum, t) => sum + t.providers.reduce((ps, p) => ps + p.instances.length, 0),
    0,
  );

  const addToast = (message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    const id = Date.now().toString();
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  const confirmRebuild = async () => {
    if (!rebuildTarget) return;
    setIsRebuilding(true);
    try {
      await apiClient.rebuildDevInstance(rebuildTarget.id, rebuildTarget.tenantCode);
      addToast(`Rebuild started: ${rebuildTarget.name}`, 'info');
      setRebuildTarget(null);
    } catch (err: any) {
      addToast(`Failed to start rebuild: ${err.message}`, 'error');
    } finally {
      setIsRebuilding(false);
    }
  };

  const confirmDestroy = async () => {
    if (!destroyTarget) return;
    setIsDestroying(true);
    try {
      const result = await apiClient.destroyInstance(destroyTarget.name);
      if (!result.canDestroy) {
        addToast(result.reason || 'Cannot destroy: no configuration found.', 'error');
      } else {
        addToast(`Destroy workflow started: ${result.runId}`, 'info');
        setDestroyTarget(null);
      }
    } catch (err: any) {
      addToast(`Failed to destroy: ${err.message}`, 'error');
    } finally {
      setIsDestroying(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Code}
        iconColor="text-cyan-600 dark:text-cyan-400"
        iconBgColor="bg-cyan-100 dark:bg-cyan-900/30"
        description="Monitor status and logs for tenant Manage dockerized instances (Hetzner + OCI)"
        count={totalInstances}
        countLabel="instances"
        countColor="text-cyan-600 dark:text-cyan-400"
        actions={canManageInstance ? (
          <DropdownMenu
            trigger={
              <Button variant="outline" size="sm" className="w-full h-full text-sm py-2">
                <Settings className="w-4 h-4 mr-2" />
                Actions
              </Button>
            }
            items={[
              {
                label: 'New Instance',
                icon: <Server className="w-4 h-4" />,
                onClick: () => navigate('/workflows', { state: { openWorkflow: 'devops-manage-deploy' } }),
              },
            ]}
          />
        ) : undefined}
      />
      <LastSynced iso={syncedAt} />
      {tenantGroups.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-gray-500 dark:text-gray-400">
            No container instances found.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {tenantGroups.map((group) => (
            <div key={group.tenant} className="space-y-4">
              <div className="flex items-center justify-between">
                <TenantHeading
                  tenant={resolveTenantMeta(tenantMetaMap, group.tenant, {
                    name: group.tenantName,
                    color: group.tenantColor,
                  })}
                  title={group.tenantName}
                />
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  {group.providers.reduce((n, p) => n + p.instances.length, 0)} instance
                  {group.providers.reduce((n, p) => n + p.instances.length, 0) !== 1 ? 's' : ''}
                </span>
              </div>

              {group.providers.map((providerGroup) => (
                <div key={`${group.tenant}-${providerGroup.provider}`} className="space-y-4 md:ml-6">
                  <div className="flex items-center gap-2 py-2 border-b border-gray-200 dark:border-gray-700">
                    <Cloud className="w-5 h-5" style={{ color: getCloudProviderColor(providerGroup.provider) }} />
                    <h3
                      className="text-base font-medium"
                      style={{ color: getCloudProviderColor(providerGroup.provider) }}
                    >
                      {providerGroup.provider.toUpperCase()}
                    </h3>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      ({providerGroup.instances.length})
                    </span>
                  </div>

              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {providerGroup.instances.map((instance) => (
                  <Card key={instance.id} className="hover:shadow-lg transition-shadow overflow-hidden">
                    <CardHeader className="space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center space-x-3 min-w-0 flex-1">
                          <div className="w-10 h-10 shrink-0 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                            <Server className="w-4 h-4" />
                          </div>
                          <CardTitle className="text-lg break-words min-w-0">{instance.name}</CardTitle>
                        </div>
                        <div className="shrink-0 flex items-center gap-2">
                          {getStatusBadge(instance.status)}
                          {canManageInstance && (
                            <DropdownMenu
                              trigger={
                                <button
                                  className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
                                  title="Actions"
                                  aria-label="Actions"
                                >
                                  <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                                </button>
                              }
                              items={[
                                {
                                  label: 'Manage',
                                  icon: <FileText className="w-4 h-4" />,
                                  onClick: () => setSelectedServer({ id: instance.id, name: instance.name }),
                                },
                                ...(!instance.name.toLowerCase().includes('ohub')
                                  ? [
                                      {
                                        label: 'Manage Login',
                                        icon: <Briefcase className="w-4 h-4" />,
                                        onClick: () =>
                                          window.open(
                                            `https://${instance.fqdn || `${instance.name}.maxiron.cloud`}/maximo`,
                                            '_blank',
                                          ),
                                      },
                                    ]
                                  : []),
                                {
                                  label: 'Rebuild Instance',
                                  icon: <RefreshCw className="w-4 h-4" />,
                                  onClick: () => setRebuildTarget({ id: instance.id, name: instance.name, tenantCode: group.tenant }),
                                  className: 'text-red-700 dark:text-red-400',
                                },
                                {
                                  label: 'Destroy Server',
                                  icon: <Trash2 className="w-4 h-4" />,
                                  onClick: () => setDestroyTarget({ id: instance.id, name: instance.name }),
                                  className: 'text-red-700 dark:text-red-400',
                                },
                              ]}
                            />
                          )}
                        </div>
                      </div>
                      <CardDescription
                        className="font-mono text-xs leading-relaxed max-w-full"
                        style={{ overflowWrap: 'anywhere', wordBreak: 'break-all' }}
                      >
                        ID: {instance.id}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between gap-2">
                          <span className="text-gray-600 dark:text-gray-400 shrink-0">Cloud:</span>
                          <span
                            className="font-medium uppercase"
                            style={{ color: getCloudProviderColor(providerGroup.provider) }}
                          >
                            {providerGroup.provider}
                          </span>
                        </div>
                        <div className="flex justify-between gap-2">
                          <span className="text-gray-600 dark:text-gray-400 shrink-0">Type:</span>
                          <span className="font-mono text-gray-900 dark:text-gray-100 text-right break-all">
                            {instance.instanceType}
                          </span>
                        </div>
                        <div className="flex justify-between gap-2">
                          <span className="text-gray-600 dark:text-gray-400 shrink-0">
                            {providerGroup.provider === 'oci' ? 'Availability domain:' : 'Datacenter:'}
                          </span>
                          <span
                            className="text-gray-900 dark:text-gray-100 text-right min-w-0"
                            style={{ overflowWrap: 'anywhere', wordBreak: 'break-all' }}
                          >
                            {instance.region}
                          </span>
                        </div>
                        {instance.privateIp && (
                          <div className="flex justify-between gap-2">
                            <span className="text-gray-600 dark:text-gray-400 shrink-0">Private IP:</span>
                            <span className="font-mono text-gray-900 dark:text-gray-100">{instance.privateIp}</span>
                          </div>
                        )}
                        {instance.publicIp && (
                          <div className="flex justify-between gap-2">
                            <span className="text-gray-600 dark:text-gray-400 shrink-0">Public IP:</span>
                            <span className="font-mono text-gray-900 dark:text-gray-100">{instance.publicIp}</span>
                          </div>
                        )}
                        <div className="text-xs text-gray-500 dark:text-gray-400 pt-2 border-t border-gray-200 dark:border-gray-700">
                          Created {formatDate(instance.created)}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
      
      {selectedServer && (
        <ContainerManagement
          serverId={selectedServer.id}
          serverName={selectedServer.name}
          isPR={false}
          onClose={() => setSelectedServer(null)}
          onDeleted={() => {
            setSelectedServer(null);
            void fetchDevInstances();
          }}
        />
      )}

      <Dialog open={!!rebuildTarget} onOpenChange={(open) => !open && setRebuildTarget(null)}>
        <DialogContent maxWidth="md">
          <DialogHeader>
            <DialogTitle>Rebuild Instance</DialogTitle>
          </DialogHeader>
          <DialogBody>
            {rebuildTarget && (
              <InlineConfirmation
                message={`Are you sure you want to rebuild "${rebuildTarget.name}"?`}
                onConfirm={confirmRebuild}
                onCancel={() => setRebuildTarget(null)}
                confirmText="Rebuild"
                cancelText="Cancel"
                isLoading={isRebuilding}
                loadingText="Starting..."
                variant="danger"
              />
            )}
          </DialogBody>
        </DialogContent>
      </Dialog>

      <Dialog open={!!destroyTarget} onOpenChange={(open) => !open && setDestroyTarget(null)}>
        <DialogContent maxWidth="md">
          <DialogHeader>
            <DialogTitle>Destroy Server</DialogTitle>
          </DialogHeader>
          <DialogBody>
            {destroyTarget && (
              <InlineConfirmation
                message={`This will trigger devops-manage-destroy for "${destroyTarget.name}". The server and all its data will be permanently deleted. Continue?`}
                onConfirm={confirmDestroy}
                onCancel={() => setDestroyTarget(null)}
                confirmText="Destroy"
                cancelText="Cancel"
                isLoading={isDestroying}
                loadingText="Submitting..."
                variant="danger"
              />
            )}
          </DialogBody>
        </DialogContent>
      </Dialog>

      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
}
